import { useEffect, useRef, useState } from "react";
import { skillsApi } from "../api/client";
import type { Skill } from "../types";
import "./SkillsView.css";
import { useToast } from "../lib/toast";

interface SkillForm {
  mode: "create" | "edit";
  name: string;
  title: string;
  content: string;
}

const EMPTY_FORM: SkillForm = {
  mode: "create",
  name: "",
  title: "",
  content: "",
};

export default function SkillsView() {
  const toast = useToast();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [expandedContent, setExpandedContent] = useState<Record<string, string>>({});
  const [form, setForm] = useState<SkillForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [skillUrl, setSkillUrl] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    try {
      setSkills(await skillsApi.list());
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startCreate = () => {
    setForm({ ...EMPTY_FORM, mode: "create" });
    setExpanded(null);
  };

  const startEdit = async (s: Skill) => {
    try {
      const res = await skillsApi.get(s.name);
      setForm({
        mode: "edit",
        name: s.name,
        title: s.name,
        content: res.content,
      });
      setExpanded(null);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const save = async () => {
    if (!form) return;
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content are required");
      return;
    }
    setSaving(true);
    try {
      if (form.mode === "create") {
        await skillsApi.create({ name: form.title.trim(), content: form.content });
      } else {
        await skillsApi.update(form.name, { content: form.content });
      }
      setForm(null);
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s: Skill) => {
    if (!confirm(`Delete skill "${s.name}"?`)) return;
    try {
      await skillsApi.remove(s.name);
      if (expanded === s.name) setExpanded(null);
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const installFiles = async (files: FileList | File[]) => {
    const file = Array.from(files)[0];
    if (!file) return;
    setInstalling(true);
    try {
      await skillsApi.installUpload(file);
      toast.success(`Installed ${file.name}`);
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setInstalling(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const installLink = async () => {
    if (!skillUrl.trim()) return;
    setInstalling(true);
    try {
      const installed = await skillsApi.installUrl(skillUrl.trim());
      setSkillUrl("");
      toast.success(`Installed ${installed.name}`);
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setInstalling(false);
    }
  };

  return (
    <div className="view">
      <div className="view-header">
        <div>
          <h2 className="view-title">⚡ Skills</h2>
          <p className="view-subtitle">Markdown capability files loaded from skills/</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={startCreate}>
          + New Skill
        </button>
      </div>

      <div className="card skill-import" onDragOver={(e) => e.preventDefault()} onDrop={(e) => {
        e.preventDefault();
        void installFiles(e.dataTransfer.files);
      }}>
        <div className="skill-import-copy">
          <strong>Install a skill</strong>
          <span className="muted">Drop a SKILL.md or .skill bundle here</span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".md,.skill,.zip,.tar,.gz"
          hidden
          onChange={(e) => void installFiles(e.target.files || [])}
        />
        <button className="btn btn-sm" onClick={() => fileInputRef.current?.click()} disabled={installing}>
          Choose file
        </button>
        <div className="skill-link-install">
          <input className="input" value={skillUrl} placeholder="Paste a SKILL.md or bundle URL" onChange={(e) => setSkillUrl(e.target.value)} />
          <button className="btn btn-primary btn-sm" onClick={() => void installLink()} disabled={installing || !skillUrl.trim()}>
            Install link
          </button>
        </div>
      </div>

      {form && (
        <div className="card fade-in skill-form" style={{ marginBottom: "var(--gap-lg)" }}>
          <div className="col">
            <div>
              <label className="form-label">Title</label>
              <input
                className="input"
                value={form.title}
                placeholder="e.g. code-reviewer"
                disabled={form.mode === "edit"}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>
            <div>
              <label className="form-label">Content (Markdown)</label>
              <textarea
                className="textarea"
                style={{ minHeight: 220, fontFamily: "var(--mono)" }}
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
              />
            </div>
            <div className="row">
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? <span className="spinner" /> : form.mode === "create" ? "Create" : "Save"}
              </button>
              <button className="btn" onClick={() => setForm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {skills.length === 0 && !form && (
        <div className="empty-state">
          <h3>No skills</h3>
          <p>Create one with the New Skill button, or add markdown files to the skills/ directory.</p>
        </div>
      )}

      <div className="col">
        {skills.map((s) => (
          <div key={s.name} className="card skill-card">
            <button
              className="skill-header"
              onClick={async () => {
                const next = expanded === s.name ? null : s.name;
                setExpanded(next);
                if (next && !(next in expandedContent)) {
                  try {
                    const res = await skillsApi.get(s.name);
                    setExpandedContent((prev) => ({ ...prev, [s.name]: res.content }));
                  } catch (e) {
                    toast.error((e as Error).message);
                  }
                }
              }}
            >
              <span className="skill-icon">⚡</span>
              <div style={{ flex: 1, textAlign: "left" }}>
                <div className="skill-name">{s.name}</div>
                <div className="skill-desc muted">{s.description}</div>
              </div>
              <span className={`skill-chevron ${expanded === s.name ? "open" : ""}`}>▾</span>
            </button>
            {expanded === s.name && expandedContent[s.name] && (
              <pre className="skill-content">{expandedContent[s.name]}</pre>
            )}
            {expanded === s.name && (
              <div className="skill-actions">
                <button className="btn btn-sm" onClick={() => startEdit(s)}>Edit</button>
                <button className="btn btn-danger btn-sm" onClick={() => remove(s)}>Delete</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
